/**
 * MacroAdvisorEngine v2 — computes a long-term macro bias for Bitcoin.
 *
 * v2 changes:
 * - Weights: 55% valuation (200WMA) / 35% trend (50WMA) / 10% chain heat
 * - Explicit valuationBucket + trendBucket (semantic ladder positions)
 * - MIXED = semantic conflict (buckets ≥2 steps apart), not generic spread
 * - Chain heat is a minor modifier, never a macro regime driver
 * - Resolution order: score → bucket → MIXED check → weighted composite → smooth
 *
 * States: DEEP_VALUE | ACCUMULATION | NEUTRAL | WARM | HOT | MIXED
 *
 * Anti-jitter: EMA smoothing + label hysteresis + persistence
 */

// ── Types ──

export type MacroLabel = 'DEEP_VALUE' | 'ACCUMULATION' | 'NEUTRAL' | 'WARM' | 'HOT' | 'MIXED';
export type MacroVisualMood = 'CALM_RESILIENT' | 'THAWING' | 'BALANCED' | 'ALERT' | 'OVERHEATED' | 'UNCERTAIN';
export type MacroSeasonState = 'WINTER' | 'THAW' | 'NEUTRAL' | 'WARM' | 'HEAT' | 'MIXED';
export type MacroConfidence = 'HIGH' | 'MEDIUM' | 'LOW';

/** Semantic bucket — same ladder as MacroLabel but without MIXED */
export type MacroBucket = 'DEEP_VALUE' | 'ACCUMULATION' | 'NEUTRAL' | 'WARM' | 'HOT';

export interface MacroAdvisorInputs {
  btcPrice: number;
  ma50w: number;
  ma200w: number;
  ma50wPrev: number;
  mempoolHeatPercentile: number; // 0..100
}

interface DerivedMetrics {
  priceVs50w: number;
  priceVs200w: number;
  ma50wSlopePct: number;
  mempoolHeatNorm: number;
}

export interface BuddyMacroState {
  macroLabel: MacroLabel;
  confidence: MacroConfidence;
  macroBiasScore: number;
  valuationScore: number;
  trendScore: number;
  chainHeatScore: number;
  /** Semantic bucket from 200WMA valuation axis */
  valuationBucket: MacroBucket;
  /** Semantic bucket from 50WMA trend axis */
  trendBucket: MacroBucket;
  /** Ladder distance between buckets (0–4) */
  bucketDistance: number;
  /** Whether MIXED was triggered by semantic conflict rule */
  mixedTriggered: boolean;
  visualMood: MacroVisualMood;
  seasonState: MacroSeasonState;
  oneLiner: string;
  bubbleTitle: string;
  bubbleBody: string;
  factors: {
    priceVs50w: number;
    priceVs200w: number;
    ma50wSlopePct: number;
    mempoolHeatPercentile: number;
  };
}

/** Persisted between runs for smoothing / hysteresis */
export interface PreviousMacroState {
  smoothedMempoolHeatNorm?: number;
  smoothedMacroBiasScore?: number;
  previousLabel?: MacroLabel;
}

// ── Helpers ──

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function ema(current: number, previous: number, alpha: number): number {
  return alpha * current + (1 - alpha) * previous;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function round4(n: number): number {
  return Math.round(n * 10000) / 10000;
}

// ── Bucket ladder ──

const BUCKET_ORDER: MacroBucket[] = ['DEEP_VALUE', 'ACCUMULATION', 'NEUTRAL', 'WARM', 'HOT'];
const BUCKET_INDEX: Record<MacroBucket, number> = {
  DEEP_VALUE: 0, ACCUMULATION: 1, NEUTRAL: 2, WARM: 3, HOT: 4,
};

function bucketDistance(a: MacroBucket, b: MacroBucket): number {
  return Math.abs(BUCKET_INDEX[a] - BUCKET_INDEX[b]);
}

// ── Derived metrics ──

function deriveMetrics(input: MacroAdvisorInputs): DerivedMetrics {
  return {
    priceVs50w: input.ma50w > 0 ? input.btcPrice / input.ma50w : 1,
    priceVs200w: input.ma200w > 0 ? input.btcPrice / input.ma200w : 1,
    ma50wSlopePct: input.ma50wPrev > 0 ? (input.ma50w - input.ma50wPrev) / input.ma50wPrev : 0,
    mempoolHeatNorm: clamp(input.mempoolHeatPercentile / 100, 0, 1),
  };
}

// ── Scoring ──

function scoreValuation(priceVs200w: number): number {
  if (priceVs200w <= 0.95) return 1.0;
  if (priceVs200w <= 1.03) return 0.9;
  if (priceVs200w <= 1.15) return 0.6;
  if (priceVs200w <= 1.35) return 0.2;
  if (priceVs200w <= 1.70) return -0.2;
  if (priceVs200w <= 2.20) return -0.6;
  return -1.0;
}

function scoreTrend(priceVs50w: number, ma50wSlopePct: number): number {
  let score = 0;
  if (priceVs50w < 0.95) score -= 0.6;
  else if (priceVs50w < 1.02) score -= 0.1;
  else if (priceVs50w < 1.15) score += 0.3;
  else score += 0.5;

  if (ma50wSlopePct < -0.02) score -= 0.4;
  else if (ma50wSlopePct < 0) score -= 0.2;
  else if (ma50wSlopePct < 0.02) score += 0.1;
  else score += 0.3;

  return clamp(score, -1, 1);
}

function scoreChainHeat(mempoolHeatNorm: number): number {
  if (mempoolHeatNorm <= 0.15) return 0.8;
  if (mempoolHeatNorm <= 0.30) return 0.5;
  if (mempoolHeatNorm <= 0.55) return 0.0;
  if (mempoolHeatNorm <= 0.75) return -0.4;
  return -0.8;
}

// ── Bucket derivation ──

function deriveBucket(score: number): MacroBucket {
  if (score >= 0.60) return 'DEEP_VALUE';
  if (score >= 0.25) return 'ACCUMULATION';
  if (score > -0.25) return 'NEUTRAL';
  if (score > -0.60) return 'WARM';
  return 'HOT';
}

// ── Semantic MIXED detection ──

function isSemanticConflict(vBucket: MacroBucket, tBucket: MacroBucket): boolean {
  const dist = bucketDistance(vBucket, tBucket);
  // Require ≥2 steps apart
  if (dist < 2) return false;
  // Both must be non-neutral to count as meaningful conflict
  if (vBucket === 'NEUTRAL' && tBucket === 'NEUTRAL') return false;
  // If one side is NEUTRAL and distance is exactly 2, not a strong conflict
  if (dist === 2 && (vBucket === 'NEUTRAL' || tBucket === 'NEUTRAL')) return false;
  return true;
}

// ── Composite (v2 weights: 55/35/10) ──

function computeCompositeScores(metrics: DerivedMetrics) {
  const valuationScore = scoreValuation(metrics.priceVs200w);
  const trendScore = scoreTrend(metrics.priceVs50w, metrics.ma50wSlopePct);
  const chainHeatScore = scoreChainHeat(metrics.mempoolHeatNorm);

  const valuationBucket = deriveBucket(valuationScore);
  const trendBucket = deriveBucket(trendScore);

  const macroBiasScore = clamp(
    valuationScore * 0.55 + trendScore * 0.35 + chainHeatScore * 0.10,
    -1, 1,
  );

  return { valuationScore, trendScore, chainHeatScore, macroBiasScore, valuationBucket, trendBucket };
}

// ── Classification (v2: semantic MIXED first, then weighted score) ──

function classifyMacroLabel(
  valuationBucket: MacroBucket,
  trendBucket: MacroBucket,
  macroBiasScore: number,
): { label: MacroLabel; mixedTriggered: boolean; dist: number } {
  const dist = bucketDistance(valuationBucket, trendBucket);
  const mixedTriggered = isSemanticConflict(valuationBucket, trendBucket);

  if (mixedTriggered) {
    return { label: 'MIXED', mixedTriggered: true, dist };
  }

  // Normal weighted resolution
  let label: MacroLabel;
  if (macroBiasScore >= 0.60) label = 'DEEP_VALUE';
  else if (macroBiasScore >= 0.25) label = 'ACCUMULATION';
  else if (macroBiasScore > -0.25) label = 'NEUTRAL';
  else if (macroBiasScore > -0.60) label = 'WARM';
  else label = 'HOT';

  return { label, mixedTriggered: false, dist };
}

// ── Confidence ──

function toDirectionalSign(score: number): -1 | 0 | 1 {
  if (score > 0.15) return 1;
  if (score < -0.15) return -1;
  return 0;
}

function classifyConfidence(
  label: MacroLabel, valuationScore: number, trendScore: number, chainHeatScore: number,
): MacroConfidence {
  if (label === 'MIXED') return 'LOW';
  const signs = [toDirectionalSign(valuationScore), toDirectionalSign(trendScore), toDirectionalSign(chainHeatScore)];
  const positives = signs.filter(s => s === 1).length;
  const negatives = signs.filter(s => s === -1).length;
  const neutrals = signs.filter(s => s === 0).length;
  if ((positives === 3 || negatives === 3) && neutrals === 0) return 'HIGH';
  if (positives >= 2 || negatives >= 2) return 'MEDIUM';
  return 'LOW';
}

// ── Visual / Season mappings ──

const VISUAL_MOOD_MAP: Record<MacroLabel, MacroVisualMood> = {
  DEEP_VALUE: 'CALM_RESILIENT', ACCUMULATION: 'THAWING', NEUTRAL: 'BALANCED',
  WARM: 'ALERT', HOT: 'OVERHEATED', MIXED: 'UNCERTAIN',
};

const SEASON_STATE_MAP: Record<MacroLabel, MacroSeasonState> = {
  DEEP_VALUE: 'WINTER', ACCUMULATION: 'THAW', NEUTRAL: 'NEUTRAL',
  WARM: 'WARM', HOT: 'HEAT', MIXED: 'MIXED',
};

// ── Copy ──

function buildCopy(label: MacroLabel): { oneLiner: string; bubbleTitle: string; bubbleBody: string } {
  switch (label) {
    case 'DEEP_VALUE': return {
      oneLiner: 'Quiet value.',
      bubbleTitle: 'Deep Value',
      bubbleBody: 'Price is near long-term support and chain activity is calm. Historically this has been a better zone for patient accumulation than panic.',
    };
    case 'ACCUMULATION': return {
      oneLiner: 'Thawing.',
      bubbleTitle: 'Accumulation',
      bubbleBody: 'Price is rebuilding above long-term support and trend conditions are improving. Chain activity is still calm enough to favor gradual accumulation over chasing.',
    };
    case 'NEUTRAL': return {
      oneLiner: 'Wait and watch.',
      bubbleTitle: 'Neutral',
      bubbleBody: 'Price is in a balanced zone relative to trend and chain activity is not extreme. This is usually a hold-your-plan environment, not a strong edge.',
    };
    case 'WARM': return {
      oneLiner: 'Getting crowded.',
      bubbleTitle: 'Warm',
      bubbleBody: 'Price is above long-term trend and chain activity is elevated. Conditions still support strength, but risk-reward is less attractive for aggressive fresh buying.',
    };
    case 'HOT': return {
      oneLiner: 'Suspiciously sunny.',
      bubbleTitle: 'Hot',
      bubbleBody: 'Price is stretched above long-term trend and on-chain activity is hot. Historically this is where discipline matters more than excitement.',
    };
    case 'MIXED': return {
      oneLiner: 'Mixed weather.',
      bubbleTitle: 'Mixed',
      bubbleBody: 'Valuation and trend are pointing in materially different directions right now. Long-term positioning says one thing, medium-term momentum says another — conviction should stay modest.',
    };
  }
}

// ── Smoothing (anti-jitter) ──

function applySmoothing(
  metrics: DerivedMetrics,
  previous?: PreviousMacroState,
): DerivedMetrics & { smoothedMempoolHeatNorm: number } {
  const prevHeat = previous?.smoothedMempoolHeatNorm ?? metrics.mempoolHeatNorm;
  const smoothedMempoolHeatNorm = ema(metrics.mempoolHeatNorm, prevHeat, 0.3);
  return {
    ...metrics,
    mempoolHeatNorm: smoothedMempoolHeatNorm,
    smoothedMempoolHeatNorm,
  };
}

function applyLabelHysteresis(
  previousLabel: MacroLabel | undefined,
  rawLabel: MacroLabel,
  smoothedMacroBiasScore: number,
): MacroLabel {
  if (!previousLabel) return rawLabel;
  if (previousLabel === 'MIXED') return rawLabel;
  if (rawLabel === 'MIXED') return rawLabel;

  // Keep previous state unless score has moved clearly into the new zone
  if (previousLabel === 'NEUTRAL' && rawLabel === 'WARM' && smoothedMacroBiasScore > -0.35) return 'NEUTRAL';
  if (previousLabel === 'NEUTRAL' && rawLabel === 'ACCUMULATION' && smoothedMacroBiasScore < 0.35) return 'NEUTRAL';
  if (previousLabel === 'ACCUMULATION' && rawLabel === 'DEEP_VALUE' && smoothedMacroBiasScore < 0.70) return 'ACCUMULATION';
  if (previousLabel === 'WARM' && rawLabel === 'HOT' && smoothedMacroBiasScore > -0.70) return 'WARM';

  return rawLabel;
}

// ── Main entry: full inputs ──

export function computeBuddyMacroState(
  input: MacroAdvisorInputs,
  previous?: PreviousMacroState,
): BuddyMacroState & { _smoothing: PreviousMacroState } {
  // 1–3. Derive metrics and scores
  const rawMetrics = deriveMetrics(input);
  const metrics = applySmoothing(rawMetrics, previous);
  const scores = computeCompositeScores(metrics);

  // 4–5. Buckets are already derived in computeCompositeScores

  // 6–7. Semantic MIXED check, then weighted resolution
  const prevSmoothedScore = previous?.smoothedMacroBiasScore ?? scores.macroBiasScore;
  const smoothedMacroBiasScore = ema(scores.macroBiasScore, prevSmoothedScore, 0.35);

  const classification = classifyMacroLabel(
    scores.valuationBucket,
    scores.trendBucket,
    smoothedMacroBiasScore,
  );

  // 8. Hysteresis
  const macroLabel = applyLabelHysteresis(previous?.previousLabel, classification.label, smoothedMacroBiasScore);

  // 9. Confidence + copy
  const confidence = classifyConfidence(macroLabel, scores.valuationScore, scores.trendScore, scores.chainHeatScore);
  const copy = buildCopy(macroLabel);

  // 10. Commit
  return {
    macroLabel,
    confidence,
    macroBiasScore: round2(smoothedMacroBiasScore),
    valuationScore: round2(scores.valuationScore),
    trendScore: round2(scores.trendScore),
    chainHeatScore: round2(scores.chainHeatScore),
    valuationBucket: scores.valuationBucket,
    trendBucket: scores.trendBucket,
    bucketDistance: classification.dist,
    mixedTriggered: classification.mixedTriggered,
    visualMood: VISUAL_MOOD_MAP[macroLabel],
    seasonState: SEASON_STATE_MAP[macroLabel],
    oneLiner: copy.oneLiner,
    bubbleTitle: copy.bubbleTitle,
    bubbleBody: copy.bubbleBody,
    factors: {
      priceVs50w: round2(rawMetrics.priceVs50w),
      priceVs200w: round2(rawMetrics.priceVs200w),
      ma50wSlopePct: round4(rawMetrics.ma50wSlopePct),
      mempoolHeatPercentile: Math.round(input.mempoolHeatPercentile),
    },
    _smoothing: {
      smoothedMempoolHeatNorm: metrics.smoothedMempoolHeatNorm,
      smoothedMacroBiasScore: round4(smoothedMacroBiasScore),
      previousLabel: macroLabel,
    },
  };
}

// ── Fallback: derive from existing backend state fields ──

export function deriveMacroFromBackendState(
  macroMode: string,
  seasonState: string,
  heatLevel: number,
  ltConfidence: string,
): BuddyMacroState {
  const modeScores: Record<string, number> = {
    'DEEP_BELOW': 0.85, 'BELOW': 0.45, 'NEAR_200WMA': 0.0, 'ABOVE': -0.35, 'FAR_ABOVE': -0.75,
  };
  const baseScore = modeScores[macroMode] ?? 0;
  const seasonAdj = seasonState === 'cold' ? 0.15 : seasonState === 'heat' ? -0.15 : 0;
  const heatAdj = heatLevel > 0.6 ? -0.1 : heatLevel < 0.3 ? 0.1 : 0;
  const macroBiasScore = clamp(baseScore + seasonAdj + heatAdj, -1, 1);

  const valuationScore = baseScore;
  const trendScore = seasonAdj > 0 ? -0.2 : seasonAdj < 0 ? 0.3 : 0;
  const chainHeatScore = heatLevel > 0.6 ? -0.4 : heatLevel < 0.3 ? 0.5 : 0;

  const valuationBucket = deriveBucket(valuationScore);
  const trendBucket = deriveBucket(trendScore);
  const classification = classifyMacroLabel(valuationBucket, trendBucket, macroBiasScore);

  const confidence: MacroConfidence = ltConfidence === 'high' ? 'HIGH' : ltConfidence === 'medium' ? 'MEDIUM' : 'LOW';
  const copy = buildCopy(classification.label);

  return {
    macroLabel: classification.label,
    confidence,
    macroBiasScore: round2(macroBiasScore),
    valuationScore: round2(valuationScore),
    trendScore: round2(trendScore),
    chainHeatScore: round2(chainHeatScore),
    valuationBucket,
    trendBucket,
    bucketDistance: classification.dist,
    mixedTriggered: classification.mixedTriggered,
    visualMood: VISUAL_MOOD_MAP[classification.label],
    seasonState: SEASON_STATE_MAP[classification.label],
    oneLiner: copy.oneLiner,
    bubbleTitle: copy.bubbleTitle,
    bubbleBody: copy.bubbleBody,
    factors: { priceVs50w: 0, priceVs200w: 0, ma50wSlopePct: 0, mempoolHeatPercentile: Math.round(heatLevel * 100) },
  };
}
